import { reconciliationResultMessages } from "@/services/messages";
import { useEffect, useId, useRef } from "react";

/**
 * Диалог подтверждения re-run для completed-набора (FR-006).
 *
 * Native `<dialog>` (как `delete-set-dialog.tsx`): ESC, focus-trap,
 * backdrop-клик встроены в браузер. Тексты — из `messages.result.run.rerun_dialog_*`.
 */
export interface RerunConfirmDialogProps {
	onConfirm: () => void;
	onCancel: () => void;
}

export function RerunConfirmDialog({ onConfirm, onCancel }: RerunConfirmDialogProps) {
	const messages = reconciliationResultMessages.run;
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="text-lg font-semibold text-slate-900">
				{messages.rerun_dialog_title}
			</h3>
			<p className="mt-2 text-sm text-slate-600">{messages.rerun_dialog_message}</p>
			<div className="mt-6 flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
				>
					{messages.rerun_dialog_cancel}
				</button>
				<button
					type="button"
					onClick={onConfirm}
					className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
				>
					{messages.rerun_dialog_confirm}
				</button>
			</div>
		</dialog>
	);
}
